import { useState, useEffect, useRef } from 'react';
import api from '../api/axiosConfig';

export const useGestorArchivos = ({ 
  carpetaId, 
  startPath, 
  titulo, 
  nombreCliente, 
  crearNotificacion, 
  notificaciones,
  entidadCuit,
  onUploadSuccess,
  addToast 
}) => {
  const [history, setHistory] = useState([]);
  const [carpetaActual, setCarpetaActual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [archivoAEliminar, setArchivoAEliminar] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [elementosOcultos, setElementosOcultos] = useState([]);

  const historyRef = useRef(history);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    if (entidadCuit) {
      fetchElementosOcultos(entidadCuit);
    }
  }, [entidadCuit]);

  const fetchElementosOcultos = async (cuit) => {
    if (!cuit) return;
    try {
      const response = await api.get(`/obtenerElementosOcultos?cuit=${cuit}`);
      if (response.data && response.data.elementosOcultos) {
        setElementosOcultos(response.data.elementosOcultos);
      }
    } catch (error) {
      console.error("Error al obtener elementos ocultos:", error);
    }
  };

  const abrirCarpeta = async (id, nombre, isGoBack = false) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/obtenerCarpetaPorId?carpetaId=${id}`);
      const data = response.data;
      
      if (!isGoBack && carpetaActual) {
        const nextHistory = [...history, { id: carpetaActual.id, nombre: carpetaActual.nombre }];
        setHistory(nextHistory);
      }

      setCarpetaActual({
        id: data.folderId || id,
        nombre: nombre,
        contenido: data.contenido_actual || []
      });
    } catch (err) {
      console.error("Error al obtener la carpeta", err);
      if (!isGoBack && carpetaActual) {
        const nextHistory = [...history, { id: carpetaActual.id, nombre: carpetaActual.nombre }];
        setHistory(nextHistory);
      }
      setCarpetaActual({
        id: id,
        nombre: nombre,
        contenido: []
      });
      setError('No se pudo cargar el contenido desde el servidor. Mostrando carpeta vacía.');
    } finally {
      setLoading(false);
    }
  };

  const refrescarCarpetaActual = async () => {
    if (!carpetaActual) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/obtenerCarpetaPorId?carpetaId=${carpetaActual.id}&nocache=true`);
      const data = response.data;
      setCarpetaActual(prev => ({
        ...prev,
        id: data.folderId || carpetaActual.id,
        contenido: data.contenido_actual || []
      }));
    } catch (err) {
      console.error("Error al refrescar la carpeta", err);
      setError('No se pudo actualizar el contenido de la carpeta.');
    } finally {
      setLoading(false);
    }
  };

  const abrirRaiz = async (isGoBack = false) => {
    if (!carpetaId) {
      setCarpetaActual(null);
      return;
    }
    
    if (startPath && startPath.length > 0) {
      setLoading(true);
      setError('');
      try {
        let currentId = carpetaId;
        let finalName = titulo || (nombreCliente ? `Archivos de ${nombreCliente}` : 'Carpeta');
        
        for (const folderName of startPath) {
          const response = await api.get(`/obtenerCarpetaPorId?carpetaId=${currentId}`);
          const data = response.data;
          const folderNameClean = folderName.replace(/^\d{2}\s*-\s*/, '').toLowerCase();
          const subfolder = (data.contenido_actual || []).find(item => {
            if (item.tipo !== 'Carpeta') return false;
            const itemName = item.nombre.toLowerCase();
            return itemName.includes(folderName.toLowerCase()) || itemName.includes(folderNameClean);
          });
          
          if (subfolder) {
            currentId = subfolder.id;
            finalName = subfolder.nombre;
          } else {
            const carpetas = (data.contenido_actual || []).filter(i => i.tipo === 'Carpeta');
            console.error('Buscando folderName:', folderName);
            console.error('Carpetas disponibles:', carpetas.map(c => c.nombre));
            setCarpetaActual(null);
            setError(`No se encontró "${folderName}". Carpetas: ${carpetas.map(c => c.nombre).join(', ') || '(vacio)'}`);
            setLoading(false);
            return;
          }
        }
        abrirCarpeta(currentId, finalName, isGoBack);
      } catch (err) {
        console.error("Error al resolver la ruta", err);
        setCarpetaActual(null);
        setError('Error al obtener la carpeta.');
        setLoading(false);
      }
    } else {
      abrirCarpeta(carpetaId, titulo || (nombreCliente ? `Archivos de ${nombreCliente}` : 'Carpeta'), isGoBack);
    }
  };

  useEffect(() => {
    if (!carpetaActual && history.length === 0 && carpetaId) {
      abrirRaiz();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carpetaId, nombreCliente]);

  const handleVolver = () => {
    if (history.length > 0) {
      const prev = history[history.length - 1];
      setHistory(prev => prev.slice(0, -1));
      abrirCarpeta(prev.id, prev.nombre, true);
    }
  };

  const handleItemClick = (item) => {
    if (item.tipo === 'Carpeta') {
      abrirCarpeta(item.id, item.nombre);
    } else {
      handlePrevisualizarArchivo(item);
    }
  };

  const handleSubirArchivo = async (e) => {
    const files = e.target ? e.target.files : e;
    if (!files || files.length === 0 || !carpetaActual) return;

    let file = files[0];
    const nombresExistentes = carpetaActual.contenido
      .filter(item => item.tipo === 'Archivo')
      .map(item => item.nombre);

    let nombreFinal = file.name;
    if (nombresExistentes.includes(nombreFinal)) {
      const base = file.name.replace(/(\.[^.]+)$/, '');
      const ext = file.name.match(/(\.[^.]+)$/)?.[1] || '';
      let counter = 1;
      while (nombresExistentes.includes(`${base} (${counter})${ext}`)) {
        counter++;
      }
      nombreFinal = `${base} (${counter})${ext}`;
      file = new File([file], nombreFinal, { type: file.type });
    }

    const formData = new FormData();
    formData.append('archivo', file);

    setLoading(true);
    try {
      await api.post(`/subirArchivo?carpetaId=${carpetaActual.id}`, formData);
      if (addToast) addToast(`Archivo subido: ${nombreFinal}`, 'success');
      if (crearNotificacion) {
        crearNotificacion([
          ...(notificaciones || []),
          { id: Date.now(), mensaje: `Nuevo archivo subido: ${nombreFinal.slice(0, 20)}`, tiempo: 'Hace un momento', leida: false }
        ]);
      }
      abrirCarpeta(carpetaActual.id, carpetaActual.nombre, true);

      if (onUploadSuccess) onUploadSuccess(nombreFinal, titulo || carpetaActual.nombre);
    } catch (err) {
      console.error('Error al subir archivo:', err);
      if (addToast) addToast('Error al subir el archivo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarClick = (archivo) => setArchivoAEliminar(archivo);
  const cancelarEliminacion = () => setArchivoAEliminar(null);

  const confirmarEliminacion = async () => {
    if (!archivoAEliminar) return;
    try {
      await api.delete(`/eliminarArchivo?archivoId=${archivoAEliminar.id}`);
      if (addToast) addToast(`Archivo eliminado: ${archivoAEliminar.nombre}`, 'error');
      if (crearNotificacion) {
        crearNotificacion([
          ...(notificaciones || []),
          { id: Date.now(), mensaje: `Archivo eliminado: ${archivoAEliminar.nombre.slice(0, 20)}`, tiempo: 'Hace un momento', leida: false }
        ]);
      }
      abrirCarpeta(carpetaActual.id, carpetaActual.nombre, true);
    } catch (err) {
      console.error("Error al eliminar el archivo:", err);
      if (addToast) addToast('Error al eliminar el archivo', 'error');
    } finally {
      setArchivoAEliminar(null);
    }
  };

  const handleDescargarArchivo = async (archivo) => {
    try {
      const response = await api.get(`/descargarArchivo?archivoId=${archivo.id}`);
      const urlDescarga = response.data.url || response.data.downloadUrl;

      if (urlDescarga) {
        const link = document.createElement('a');
        link.href = urlDescarga;
        link.setAttribute('download', archivo.nombre || 'documento');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('El servidor no devolvió una URL de descarga válida.');
      }
    } catch (error) {
      console.error("Error al obtener el link de descarga:", error);
    }
  };

  const handlePrevisualizarArchivo = async (archivo) => {
    try {
      setPreviewFile(archivo);
      setPreviewUrl('');
      setPreviewModalOpen(true);
      
      const response = await api.get(`/previsualizarArchivo?archivoId=${archivo.id}`);
      if (response.data && response.data.url) {
        setPreviewUrl(response.data.url);
      } else {
        alert("No se pudo obtener la vista previa del archivo.");
        setPreviewModalOpen(false);
      }
    } catch (error) {
      console.error("Error al obtener la vista previa:", error);
      alert("Hubo un error al intentar abrir la vista previa.");
      setPreviewModalOpen(false);
    }
  };

  const handleToggleVisibilidad = async (archivo) => {
    if (!entidadCuit) return;
    const elementoOculto = elementosOcultos.find(e => e.id === archivo.id);
    const estaOculto = !!elementoOculto;
    const accion = estaOculto ? 'mostrar' : 'ocultar';
    const mensaje = accion === 'ocultar' ? 'ocultado' : 'mostrado';
    try {
      await api.post('/toggleVisibilidadElemento', {
        onedrive_item_id: archivo.id,
        accion: accion,
        cuit: entidadCuit
      });
      await fetchElementosOcultos(entidadCuit);
      if (addToast) addToast(`Archivo ${mensaje}: ${archivo.nombre}`, 'warning');
      if (crearNotificacion) {
        crearNotificacion([
          ...(notificaciones || []),
          { id: Date.now(), mensaje: `Archivo ${mensaje}: ${archivo.nombre.slice(0, 20)}`, tiempo: 'Hace un momento', leida: false }
        ]);
      }
    } catch (err) {
      console.error("Error al cambiar visibilidad:", err);
      if (addToast) addToast('Error al cambiar visibilidad', 'error');
    }
  };

  const contenidoMixto = carpetaActual ? carpetaActual.contenido : [];

  return {
    history,
    carpetaActual,
    loading,
    error,
    archivoAEliminar,
    previewModalOpen,
    setPreviewModalOpen,
    previewUrl,
    previewFile,
    contenidoMixto,
    elementosOcultos,
    
    abrirCarpeta,
    refrescarCarpetaActual,
    handleVolver,
    handleItemClick,
    handleSubirArchivo,
    handleEliminarClick,
    confirmarEliminacion,
    cancelarEliminacion,
    handleDescargarArchivo,
    handlePrevisualizarArchivo,
    handleToggleVisibilidad
  };
};
